// Libs
import { memo, useCallback, useLayoutEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GetQuickFilterTextParams,
  GridReadyEvent,
  ICellRendererParams,
  ValueGetterParams,
  GridApi,
  ValueGetterFunc,
  ValueFormatterParams,
} from "ag-grid-community";
import isEqual from "lodash/isEqual";
import { MdInfo } from "react-icons/md";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import axios from "axios";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";

// Local
import {
  SuspendedMembershipData,
  UnlinkedInvoiceData,
  UnlinkedTaskData,
} from "./MembershipIssuesContainer";
import * as strings from "../../strings";
import {
  convertToReadableTimestamp,
  convertToReadableTimestampDate,
} from "../../assets/js/convertToReadableTimestamp";
import SearchBox from "../../components/SearchBox";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import { StyledTooltip } from "../../components/StyledTooltip";
import { ButtonViewCustomer } from "../../components/ButtonViewCustomer";
import { ButtonViewMembership } from "../../components/ButtonViewMembership";
import { ButtonViewInvoice } from "../../components/ButtonViewInvoice";
import StyledMessage from "../../components/StyledMessage";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import BaseModal from "../../components/BaseModal";
import { ButtonRetry } from "../../components/ButtonRetry";
import HeadingOne from "../../components/HeadingOne";
import { getReadableStiltInvoiceStatus } from "../../models/invoice";
import TaskTypeCustomFilter from "../../components/tables/TaskTypeCustomFilter";
import { getReadableTaskType } from "../../models/task-types";
import TaskStatusCustomFilter from "../../components/tables/TaskStatusCustomFilter";
import { getReadableTaskStatus } from "../../models/task-status";
import { getTaskStatusPillClasses } from "../../assets/js/tasks";
import {
  ExistingMembership,
  getReadableMembershipStatus,
} from "../../models/membership";
interface Props {
  suspendedMembershipTableData: SuspendedMembershipData[];
  unlinkedInvoices: UnlinkedInvoiceData[];
  unlinkedTasks: UnlinkedTaskData[];
  userDisplayNamesMap: Record<string, string>;
  openInvoiceDialog: (invoiceID: string) => void;
  collectDues: (membershipID: string) => Promise<void>;
  retryRenew: (membershipID: string) => Promise<void>;
  linkInvoiceToMembership: (
    invoiceID: string,
    membershipID: string,
  ) => Promise<void>;
  linkTaskToMembership: (taskID: string, membershipID: string) => Promise<void>;
  isLinkingTask: string | null;
}

type TabType = "suspended" | "unlinkedInvoices" | "unlinkedTasks";

export default function MembershipIssuesPage(props: Props) {
  const [suspendedMembershipsGridReady, setSuspendedMembershipsGridReady] =
    useState(false);
  const [unlinkedInvoicesGridReady, setUnlinkedInvoicesGridReady] =
    useState(false);
  const [unlinkedTasksGridReady, setUnlinkedTasksGridReady] = useState(false);
  const suspendedMembershipsGridRef = useRef<AgGridReact>(null);
  const unlinkedInvoicesGridRef = useRef<AgGridReact>(null);
  const unlinkedTasksGridRef = useRef<AgGridReact>(null);
  const [activeTab, setActiveTab] = useState<TabType>("suspended");

  const [moreInfoDialogOpen, setMoreInfoDialogOpen] = useState(false);
  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  const onGridReady = useCallback(() => {
    setSuspendedMembershipsGridReady(true);
  }, []);

  const onUnlinkedInvoicesGridReady = useCallback(() => {
    setUnlinkedInvoicesGridReady(true);
  }, []);

  const onUnlinkedTasksGridReady = useCallback(() => {
    setUnlinkedTasksGridReady(true);
  }, []);

  useLayoutEffect(() => {
    if (!suspendedMembershipsGridReady) return;
    if (suspendedMembershipsGridRef.current) {
      const api: GridApi = suspendedMembershipsGridRef.current.api;
      api?.sizeColumnsToFit();
    }
  }, [suspendedMembershipsGridReady]);

  useLayoutEffect(() => {
    if (!unlinkedInvoicesGridReady) return;
    if (unlinkedInvoicesGridRef.current) {
      const api: GridApi = unlinkedInvoicesGridRef.current.api;
      api?.sizeColumnsToFit();
    }
  }, [unlinkedInvoicesGridReady]);

  useLayoutEffect(() => {
    if (!unlinkedTasksGridReady) return;
    if (unlinkedTasksGridRef.current) {
      const api: GridApi = unlinkedTasksGridRef.current.api;
      api?.sizeColumnsToFit();
    }
  }, [unlinkedTasksGridReady]);

  const onFilterTextBoxChanged = useCallback(() => {
    if (
      activeTab === "suspended" &&
      suspendedMembershipsGridReady &&
      suspendedMembershipsGridRef.current
    ) {
      suspendedMembershipsGridRef.current.api.setGridOption(
        "quickFilterText",
        (document.getElementById("filter-text-box") as HTMLInputElement).value,
      );
    } else if (
      activeTab === "unlinkedInvoices" &&
      unlinkedInvoicesGridReady &&
      unlinkedInvoicesGridRef.current
    ) {
      unlinkedInvoicesGridRef.current.api.setGridOption(
        "quickFilterText",
        (document.getElementById("filter-text-box") as HTMLInputElement).value,
      );
    } else if (
      activeTab === "unlinkedTasks" &&
      unlinkedTasksGridReady &&
      unlinkedTasksGridRef.current
    ) {
      unlinkedTasksGridRef.current.api.setGridOption(
        "quickFilterText",
        (document.getElementById("filter-text-box") as HTMLInputElement).value,
      );
    }
  }, [
    activeTab,
    suspendedMembershipsGridReady,
    unlinkedInvoicesGridReady,
    unlinkedTasksGridReady,
  ]);

  // could move this and the other dialog gazz up to the container
  const moreInfoButton = (
    <BaseButtonSecondary onClick={() => setMoreInfoDialogOpen(true)}>
      {strings.SHOW_MORE_INFO}
    </BaseButtonSecondary>
  );

  async function onRetryAction(
    membershipID: string,
    isPendingRenewal?: boolean,
  ): Promise<void> {
    try {
      if (isPendingRenewal) {
        await props.retryRenew(membershipID);
      } else {
        await props.collectDues(membershipID);
      }
      addMessage({
        id: createToastMessageID(),
        type: "success",
        dialog: false,
        message: isPendingRenewal
          ? strings.RENEW_MEMBERSHIP_SUCCESS
          : strings.COLLECT_DUES_SUCCESS,
      });
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const status = e.response?.data?.status;
        if (
          (status === 409 || status === 422 || status === 403) &&
          typeof e.response?.data?.message === "string"
        ) {
          // 409 Conflict. 422 Unprocessable Entity. 403 Authorization. show the message
          addMessage({
            id: createToastMessageID(),
            type: "error",
            dialog: false,
            message: e.response.data.message,
          });
          return;
        }
      }
      addMessage({
        id: createToastMessageID(),
        type: "error",
        dialog: false,
        message: isPendingRenewal
          ? strings.RENEW_MEMBERSHIP_FAIL
          : strings.COLLECT_DUES_FAIL,
      });
    }
  }

  return (
    <div>
      <span className="mb-2 inline-block">
        <HeadingOne>{strings.MEMBERSHIP_ISSUES}</HeadingOne>
      </span>

      {/* Tab Navigation */}
      <div className="mb-4 border-b border-gray-200">
        <ul className="-mb-px flex flex-wrap text-center text-sm font-medium text-gray-500">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab("suspended")}
              className={`inline-flex items-center rounded-t-lg border-b-2 p-4 ${
                activeTab === "suspended"
                  ? "border-primary text-primary"
                  : "border-transparent hover:border-gray-300 hover:text-gray-600"
              }`}
            >
              Suspended Memberships
              <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                {props.suspendedMembershipTableData.length}
              </span>
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab("unlinkedInvoices")}
              className={`inline-flex items-center rounded-t-lg border-b-2 p-4 ${
                activeTab === "unlinkedInvoices"
                  ? "border-primary text-primary"
                  : "border-transparent hover:border-gray-300 hover:text-gray-600"
              }`}
            >
              Unlinked Invoices
              <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                {props.unlinkedInvoices.length}
              </span>
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab("unlinkedTasks")}
              className={`inline-flex items-center rounded-t-lg border-b-2 p-4 ${
                activeTab === "unlinkedTasks"
                  ? "border-primary text-primary"
                  : "border-transparent hover:border-gray-300 hover:text-gray-600"
              }`}
            >
              Unlinked Tasks
              <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                {props.unlinkedTasks.length}
              </span>
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      {activeTab === "suspended" && (
        <>
          <h3>{`${props.suspendedMembershipTableData.length} Suspended Membership${props.suspendedMembershipTableData.length === 1 ? "" : "s"}`}</h3>
          <StyledMessage
            type="info"
            dismissible={false}
            overrideTextAlignment="text-left"
            overrideIconAlignment="self-start mt-1"
            overridePadding="px-3 py-2"
          >
            {{
              message:
                "Suspended memberships occur when: 1) an automatic payment collection fails, 2) an automatic membership renewal cannot be processed, or 3) the membership is manually placed in a suspended status. Each row in the table represents a suspended membership. If the membership was not manually suspended, the row will include additional information to help you take action.",
              icon: <MdInfo />,
              button: moreInfoButton,
            }}
          </StyledMessage>

          <div className="pt-4">
            <SearchBox
              onInput={onFilterTextBoxChanged}
              widthClasses="max-w-sm w-full"
            />
          </div>
          <div className={"ag-theme-alpine h-[1000px] max-h-[54vh] pb-6"}>
            <IssuesTable
              suspendedMembershipTableData={props.suspendedMembershipTableData}
              openInvoiceDialog={props.openInvoiceDialog}
              // goToCustomerPage={props.goToCustomerPage}
              // goToMembership={props.goToMembership}
              onRetryAction={onRetryAction}
              gridRef={suspendedMembershipsGridRef}
              onGridReady={onGridReady}
              userDisplayNamesMap={props.userDisplayNamesMap}
              isLinkingTask={props.isLinkingTask}
            />
          </div>
        </>
      )}

      {activeTab === "unlinkedInvoices" && (
        <>
          <h3>{`${props.unlinkedInvoices.length} Unlinked Invoice${props.unlinkedInvoices.length === 1 ? "" : "s"}`}</h3>
          <StyledMessage
            type="info"
            dismissible={false}
            overrideTextAlignment="text-left"
            overrideIconAlignment="self-start mt-1"
          >
            {{
              message:
                "These invoices contain membership-related line items but are not linked to any membership. Linking these invoices to the correct membership helps maintain accurate financial records and ensures proper tracking of membership payments.",
              icon: <MdInfo />,
            }}
          </StyledMessage>

          <div className="pt-4">
            <SearchBox
              onInput={onFilterTextBoxChanged}
              widthClasses="max-w-sm w-full"
            />
          </div>
          <div className={"ag-theme-alpine h-[1000px] max-h-[54vh] pb-6"}>
            <UnlinkedInvoicesTable
              unlinkedInvoices={props.unlinkedInvoices}
              openInvoiceDialog={props.openInvoiceDialog}
              goToCustomerPage={() => {}}
              gridRef={unlinkedInvoicesGridRef}
              onGridReady={onUnlinkedInvoicesGridReady}
              linkInvoiceToMembership={props.linkInvoiceToMembership}
            />
          </div>
        </>
      )}

      {activeTab === "unlinkedTasks" && (
        <>
          <h3>{`${props.unlinkedTasks.length} Unlinked Task${props.unlinkedTasks.length === 1 ? "" : "s"}`}</h3>
          <StyledMessage
            type="info"
            dismissible={false}
            overrideTextAlignment="text-left"
            overrideIconAlignment="self-start mt-1"
          >
            {{
              message:
                "These tasks have membership-related task types but are not properly linked to any membership. Linking these tasks ensures proper tracking of membership services and helps maintain consistent service delivery records.",
              icon: <MdInfo />,
            }}
          </StyledMessage>

          <div className="pt-4">
            <SearchBox
              onInput={onFilterTextBoxChanged}
              widthClasses="max-w-sm w-full"
            />
          </div>
          <div className={"ag-theme-alpine h-[1000px] max-h-[54vh] pb-6"}>
            <UnlinkedTasksTable
              unlinkedTasks={props.unlinkedTasks}
              goToCustomerPage={() => {}}
              gridRef={unlinkedTasksGridRef}
              onGridReady={onUnlinkedTasksGridReady}
              linkTaskToMembership={props.linkTaskToMembership}
              isLinkingTask={props.isLinkingTask}
            />
          </div>
        </>
      )}

      <MoreInfoDialog
        closeDialog={() => setMoreInfoDialogOpen(false)}
        isOpen={moreInfoDialogOpen}
      />
    </div>
  );
}

const commonColProps = {
  // minWidth: 150,
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  // wrapText: true,
  autoHeight: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  headerClass:
    "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
};

type TableProps = {
  gridRef: React.MutableRefObject<AgGridReact | null>;
  onGridReady: (event: GridReadyEvent) => void;
  onRetryAction: (
    membershipID: string,
    isPendingRenewal?: boolean,
  ) => Promise<void>;
} & Omit<
  Props,
  | "unlinkedInvoices"
  | "unlinkedTasks"
  | "collectDues"
  | "retryRenew"
  | "linkInvoiceToMembership"
  | "linkTaskToMembership"
>;

/* MEMOIZED TABLE COMPONENT */
const IssuesTable = memo(
  (props: TableProps) => {
    const renderMessage = (params: ICellRendererParams): JSX.Element => {
      return (
        <div className="text-wrap py-3 leading-snug">{params.data.message}</div>
      );
    };

    const renderSuspendedBy: ValueGetterFunc = useCallback(
      function (params: ValueGetterParams) {
        const displayName = props.userDisplayNamesMap[params.data.suspendedBy];
        if (!displayName) return "";
        return displayName;
      },
      [props.userDisplayNamesMap],
    );

    // so the renderIconCell useCallback dependency array doesn't need to include all props:
    const openInvoiceDialog = props.openInvoiceDialog;
    const onRetryAction = props.onRetryAction;

    const renderIconCell = useCallback(
      function (params: ICellRendererParams) {
        return (
          <div className="mt-1 flex max-w-fit items-center gap-0.5">
            {typeof params.data.pendingInvoiceID === "string" && (
              <StyledTooltip title="View Invoice">
                <ButtonViewInvoice
                  onClick={() =>
                    openInvoiceDialog(params.data.pendingInvoiceID)
                  }
                />
              </StyledTooltip>
            )}

            <StyledTooltip title="View Customer">
              <ButtonViewCustomer customerID={params.data.customerID} />
            </StyledTooltip>

            <StyledTooltip title="View Membership">
              <ButtonViewMembership membershipID={params.data.membershipID} />
            </StyledTooltip>

            {/* if there's no suspensionData, this was manually suspended. don't show the Retry button */}
            {params.data.hasSuspensionData ? (
              <StyledTooltip
                title={`${params.data.isPendingRenewal ? "Renew" : "Collect Dues"}`}
              >
                <ButtonRetry
                  onClick={async () =>
                    await onRetryAction(
                      params.data.membershipID,
                      params.data.isPendingRenewal,
                    )
                  }
                />
              </StyledTooltip>
            ) : (
              // better alignment / better UI ... imo
              <div className="w-[38px]" />
            )}
          </div>
        );
      },
      [onRetryAction, openInvoiceDialog],
    );

    const columnDefs: ColDef[] = [
      {
        field: "customerName",
        headerName: "Customer",
        flex: 2,
      },
      {
        field: "message",
        headerName: "Message",
        cellRenderer: renderMessage,
        autoHeight: true,
        minWidth: 200,
        flex: 3,
      },
      {
        field: "timestampSuspended",
        headerName: "Time Suspended",
        cellRenderer: getReadableSuspendedTimestamp,
        getQuickFilterText: getReadableSuspendedTimestamp,
      },
      {
        field: "suspendedBy",
        headerName: "Suspended By",
        valueGetter: renderSuspendedBy,
      },
      {
        cellRenderer: renderIconCell,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        sortable: false,
        minWidth: 180,
        maxWidth: 180,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "start",
          justifyContent: "end",
        },
      },
    ];

    return (
      <AgGridReact
        reactiveCustomComponents
        ref={props.gridRef}
        onGridReady={props.onGridReady}
        defaultColDef={commonColProps}
        className="mt-5 shadow"
        rowData={props.suspendedMembershipTableData}
        animateRows={true}
        rowSelection="single"
        enableCellTextSelection={true}
        suppressRowClickSelection={true}
        rowHeight={50}
        cacheQuickFilter={true}
        columnDefs={columnDefs}
      />
    );
  },
  (previous, next) =>
    isEqual(
      previous.suspendedMembershipTableData,
      next.suspendedMembershipTableData,
    ),
);

type UnlinkedInvoicesTableProps = {
  unlinkedInvoices: UnlinkedInvoiceData[];
  openInvoiceDialog: (invoiceID: string) => void;
  goToCustomerPage: (customerID: string) => void;
  gridRef: React.MutableRefObject<AgGridReact | null>;
  onGridReady: (event: GridReadyEvent) => void;
  linkInvoiceToMembership: (
    invoiceID: string,
    membershipID: string,
  ) => Promise<void>;
};

/* UNLINKED INVOICES TABLE COMPONENT */
const UnlinkedInvoicesTable = memo(
  (props: UnlinkedInvoicesTableProps) => {
    const openInvoiceDialog = props.openInvoiceDialog;
    const goToCustomerPage = props.goToCustomerPage;
    const linkInvoiceToMembership = props.linkInvoiceToMembership;

    const formatCurrency = (params: ValueGetterParams) => {
      const value = params.data.amount;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    };

    const formatDate = (params: ValueGetterParams) => {
      if (!params.data.issueDate) return "N/A";
      return convertToReadableTimestampDate(params.data.issueDate);
    };

    const renderInvoiceNumberAndStatus = (
      params: ICellRendererParams,
    ): JSX.Element => {
      let customBg = "bg-primary text-primaryButtonText";
      switch (params.data.invoiceStatus) {
        case "paid":
          customBg = "bg-greenPass text-white";
          break;
        case "partiallyPaid":
          customBg = "bg-orangeError text-white";
          break;
        case "canceled":
          customBg = "bg-gray-200 text-black";
          break;
        case "draft":
          customBg = "bg-gray-200 text-black";
          break;
      }
      return (
        <div className="flex items-center space-x-4">
          {params.data.invoiceNumber}
          <div
            className={`m-1 rounded-full px-2.5 pb-1 pt-0.5 text-xs font-medium ${customBg} capitalize`}
          >
            {getReadableStiltInvoiceStatus(params.data.invoiceStatus)}
          </div>
        </div>
      );
    };

    const renderSuggestedMembership = (
      params: ICellRendererParams,
    ): JSX.Element => {
      const suggestedMembership = params.data.suggestedMembership;

      let suggestedMembershipDescription = "";
      if (suggestedMembership) {
        const startDateString = suggestedMembership.membershipStartDate
          ? convertToReadableTimestampDate(
              suggestedMembership.membershipStartDate,
            )
          : "";
        const endDateString = suggestedMembership.membershipEndDate
          ? convertToReadableTimestampDate(
              suggestedMembership.membershipEndDate,
            )
          : "";
        suggestedMembershipDescription = `${getReadableMembershipStatus(suggestedMembership.status)} - ${startDateString} to ${endDateString}`;
      }

      return <div>{suggestedMembershipDescription}</div>;
    };

    const renderActionsCell = useCallback(
      function (params: ICellRendererParams) {
        return (
          <div className="mt-1 flex max-w-fit items-center gap-0.5">
            <StyledTooltip title="View Invoice">
              <ButtonViewInvoice
                onClick={() => openInvoiceDialog(params.data.id)}
              />
            </StyledTooltip>

            <StyledTooltip title="View Customer">
              <ButtonViewCustomer
                customerID={params.data.customerID}
                // onClick={() => goToCustomerPage(params.data.customerID)}
              />
            </StyledTooltip>

            {params.data.suggestedMembership?.id && (
              <button
                onClick={() =>
                  linkInvoiceToMembership(
                    params.data.id,
                    params.data.suggestedMembership.id,
                  )
                }
                className="ml-2 flex h-8 items-center rounded-md bg-green-100 px-2 text-green-700 hover:bg-green-200"
                title="Link to Suggested Membership"
              >
                Link
              </button>
            )}
          </div>
        );
      },
      [openInvoiceDialog, goToCustomerPage, linkInvoiceToMembership],
    );

    const columnDefs: ColDef[] = [
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        minWidth: 150,
        cellRenderer: renderInvoiceNumberAndStatus,
        // flex: 1,
      },
      {
        field: "customerName",
        headerName: "Customer",
        flex: 2,
      },
      {
        field: "amount",
        headerName: "Amount",
        valueGetter: formatCurrency,
        flex: 1,
      },
      {
        field: "membershipItemName",
        headerName: "Membership Item",
        flex: 2,
      },
      {
        field: "issueDate",
        headerName: "Invoice Date",
        valueGetter: formatDate,
        flex: 1,
      },
      {
        field: "suggestedMembership",
        headerName: "Suggested Membership",
        cellRenderer: renderSuggestedMembership,
        flex: 2,
      },
      {
        cellRenderer: renderActionsCell,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        sortable: false,
        minWidth: 180,
        maxWidth: 180,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "start",
          justifyContent: "end",
        },
      },
    ];

    return (
      <AgGridReact
        reactiveCustomComponents
        ref={props.gridRef}
        onGridReady={props.onGridReady}
        defaultColDef={commonColProps}
        className="mt-5 shadow"
        rowData={props.unlinkedInvoices}
        animateRows={true}
        rowSelection="single"
        enableCellTextSelection={true}
        suppressRowClickSelection={true}
        rowHeight={50}
        cacheQuickFilter={true}
        columnDefs={columnDefs}
      />
    );
  },
  (previous, next) => isEqual(previous.unlinkedInvoices, next.unlinkedInvoices),
);

type UnlinkedTasksTableProps = {
  unlinkedTasks: UnlinkedTaskData[];
  goToCustomerPage: (customerID: string) => void;
  gridRef: React.MutableRefObject<AgGridReact | null>;
  onGridReady: (event: GridReadyEvent) => void;
  linkTaskToMembership: (taskID: string, membershipID: string) => Promise<void>;
  isLinkingTask: string | null;
};

/* UNLINKED TASKS TABLE COMPONENT */
const UnlinkedTasksTable = memo(
  (props: UnlinkedTasksTableProps) => {
    const goToCustomerPage = props.goToCustomerPage;
    const linkTaskToMembership = props.linkTaskToMembership;
    const isLinkingTask = props.isLinkingTask;
    const formatDate = (params: ValueGetterParams) => {
      if (!params.data.timestampScheduled) return "N/A";
      return convertToReadableTimestampDate(params.data.timestampScheduled);
    };

    const renderTaskType = (params: ICellRendererParams): JSX.Element => {
      return <div>{getReadableTaskType(params.data.taskType)}</div>;
    };

    const readableTaskStatus = (params: ICellRendererParams): JSX.Element => {
      return (
        <div className="flex items-center">
          <div
            className={`m-1 rounded-full px-2.5 pb-1 pt-0.5 text-xs font-medium ${getTaskStatusPillClasses(params.data.taskStatus)}`}
          >
            {getReadableTaskStatus(params.data.taskStatus)}
          </div>
        </div>
      );
    };

    const renderSuggestedMembership = (
      params: ICellRendererParams,
    ): JSX.Element => {
      const suggestedMembership =
        (params.data.suggestedMembership as ExistingMembership | undefined) ||
        null;

      let suggestedMembershipDescription = "";
      if (suggestedMembership) {
        const startDateString = suggestedMembership.membershipStartDate
          ? convertToReadableTimestampDate(
              suggestedMembership.membershipStartDate,
            )
          : "";
        const endDateString = suggestedMembership.membershipEndDate
          ? convertToReadableTimestampDate(
              suggestedMembership.membershipEndDate,
            )
          : "";
        suggestedMembershipDescription = `${getReadableMembershipStatus(suggestedMembership.status)} - ${startDateString} to ${endDateString}`;
      }

      const numTasks = suggestedMembership?.tasks
        ? Object.keys(suggestedMembership.tasks).length
        : 0;

      if (numTasks === 0) {
        return <div>{suggestedMembershipDescription}</div>;
      }

      return (
        <div className="flex flex-col items-start justify-center space-y-0 py-1">
          <div className="my-0 py-0 text-sm">
            {suggestedMembershipDescription}
          </div>
          <span className="py-0 text-sm text-gray-500">
            ({numTasks} tasks already linked)
          </span>
        </div>
      );
    };

    const renderActionsCell = useCallback(
      function (params: ICellRendererParams) {
        return (
          <div className="mt-1 flex max-w-fit items-center gap-0.5">
            <StyledTooltip title="View Customer">
              <ButtonViewCustomer
                customerID={params.data.customerID}
                // onClick={() => goToCustomerPage(params.data.customerID)}
              />
            </StyledTooltip>

            {params.data.suggestedMembership?.id && (
              <button
                onClick={() =>
                  linkTaskToMembership(
                    params.data.id,
                    params.data.suggestedMembership.id,
                  )
                }
                className="ml-2 flex h-8 items-center rounded-md bg-green-100 px-2 text-green-700 hover:bg-green-200"
                title="Link to Suggested Membership"
                disabled={props.isLinkingTask === params.data.id}
              >
                {props.isLinkingTask === params.data.id ? (
                  <span>Linking...</span>
                ) : (
                  <span>Link</span>
                )}
              </button>
            )}
          </div>
        );
      },
      [goToCustomerPage, linkTaskToMembership],
    );

    const columnDefs: ColDef[] = [
      {
        field: "title",
        headerName: "Task Title",
        minWidth: 200,
        flex: 3,
      },
      {
        field: "taskStatus",
        headerName: "Status",
        filter: TaskStatusCustomFilter,
        valueFormatter: (params: ValueFormatterParams) => params.value,
        cellRenderer: readableTaskStatus,
        tooltipValueGetter: (params) =>
          getReadableTaskStatus(params.data.taskStatus),
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return getReadableTaskStatus(params.data.taskStatus);
        },
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "taskType",
        headerName: "Task Type",
        filter: TaskTypeCustomFilter,
        valueFormatter: (params: ValueFormatterParams) => params.value,
        cellRenderer: renderTaskType,
        tooltipValueGetter: (params) =>
          getReadableTaskType(params.data.taskType),
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return getReadableTaskType(params.data.taskType);
        },
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "timestampScheduled",
        headerName: "Scheduled Date",
        valueGetter: formatDate,
        flex: 1,
      },
      {
        field: "suggestedMembership",
        headerName: "Suggested Membership",
        cellRenderer: renderSuggestedMembership,
        flex: 2,
      },
      {
        cellRenderer: renderActionsCell,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        sortable: false,
        minWidth: 180,
        maxWidth: 180,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "start",
          justifyContent: "end",
        },
      },
    ];

    return (
      <AgGridReact
        reactiveCustomComponents
        ref={props.gridRef}
        onGridReady={props.onGridReady}
        defaultColDef={commonColProps}
        className="mt-5 shadow"
        rowData={props.unlinkedTasks}
        animateRows={true}
        rowSelection="single"
        enableCellTextSelection={true}
        suppressRowClickSelection={true}
        rowHeight={50}
        cacheQuickFilter={true}
        columnDefs={columnDefs}
      />
    );
  },
  (previous, next) => isEqual(previous.unlinkedTasks, next.unlinkedTasks),
);

function getReadableSuspendedTimestamp(
  params: ICellRendererParams | ValueGetterParams | GetQuickFilterTextParams,
): string {
  if (params.data?.timestampSuspended) {
    return convertToReadableTimestamp(params.data.timestampSuspended);
  }
  return "";
}

function MoreInfoDialog(props: {
  closeDialog: () => void;
  isOpen: boolean;
}): JSX.Element {
  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <h1 className="inline-flex items-center text-xl font-semibold">
        {strings.MORE_INFO}
      </h1>
      <button type="button" onClick={props.closeDialog}>
        <XMarkIcon
          aria-label={`close membership issues more information dialog`}
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      closeModal={props.closeDialog}
      open={props.isOpen}
      title={header}
      parentDivStyles="text-left max-w-5xl"
    >
      <div className="px-8 pb-8">
        <h4 className="text-lg font-medium">
          How to Resolve Suspended Memberships
        </h4>
        <p className="my-1">
          To get a membership out of the suspended status, you can take one of
          the following actions:
        </p>
        <ul className="list-disc space-y-1 pl-8">
          <li>
            Retry Actions (Renew or Collect Dues): This will attempt to process
            the renewal and/or outstanding payment again. For example, if a
            customer's primary card on file was declined because it is expired,
            you can select a new primary card, and then click the "renew" or
            "collect dues" button within the table row. (You can find a button
            to manage cards on the membership or customer page.)
          </li>
          <li>
            Manually: The membership's status can be changed via the edit
            membership dialog. Click the pencil icon on the membership page to
            do this.
          </li>
          <li>
            Pay the Pending Invoice: If there is a pending invoice linked to the
            membership, paying the invoice will automatically reactivate the
            membership.
          </li>
        </ul>
        <h4 className="mb-1 mt-4 text-lg font-medium">
          Additional Information and Tips
        </h4>
        <ul className="list-disc space-y-1 pl-8">
          <li>
            If an invoice has been automatically generated for the membership,
            there will be an option to view the invoice directly from the table.
          </li>
          <li>
            Each row in the table provides three additional actionable options:
            view the membership, view the customer, and retry renewal (or
            collect dues, depending on where the given membership is in its
            lifecycle).
          </li>
          <li>
            You may also find a message field that explains the suspension
            reason and suggests next steps.
          </li>
          <li>
            If you're unsure how to proceed, refer to the actions available in
            the table or reach out to support for assistance.
          </li>
        </ul>
      </div>
    </BaseModal>
  );
}
